import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, RegisteredStore } from "@/contexts/AuthContext";
import {
  Shield, Store, Search, ToggleLeft, ToggleRight, Trash2,
  CheckCircle, XCircle, Clock, AlertTriangle, LogOut, Sparkles,
  MapPin, Mail, Phone, User, Calendar, ChevronDown, ChevronUp, CalendarPlus
} from "lucide-react";
import "./SystemAdmin.css";

function getTrialInfo(store: RegisteredStore) {
  const start = new Date(store.trialStartDate);
  const end = new Date(start.getTime() + store.trialDays * 24 * 60 * 60 * 1000);
  const now = new Date();
  const msLeft = end.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
  return { start, end, daysLeft, isExpired: msLeft <= 0 };
}

function formatDate(date: Date | string) {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function SystemAdmin() {
  const { user, logout, getRegisteredStores, fetchStores, updateStoreStatus, extendTrial, deleteStore } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "trial" | "expired" | "disabled">("all");
  const [expandedStore, setExpandedStore] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [extendTrialStore, setExtendTrialStore] = useState<string | null>(null);
  const [extendDays, setExtendDays] = useState(7);

  useEffect(() => {
    fetchStores();
  }, []);

  const stores = getRegisteredStores();

  const filteredStores = stores.filter((s) => {
    const matchesSearch =
      s.storeName.toLowerCase().includes(search.toLowerCase()) ||
      s.email.toLowerCase().includes(search.toLowerCase()) ||
      s.username.toLowerCase().includes(search.toLowerCase()) ||
      `${s.firstName} ${s.lastName}`.toLowerCase().includes(search.toLowerCase());

    if (!matchesSearch) return false;

    if (filterStatus === "disabled") return s.disabled;
    if (filterStatus === "all") return true;
    if (s.disabled) return false;
    if (filterStatus === "active") return s.plan === "active";
    if (filterStatus === "trial") return s.plan === "trial" && !getTrialInfo(s).isExpired;
    if (filterStatus === "expired") return s.plan === "expired" || (s.plan === "trial" && getTrialInfo(s).isExpired);
    return true;
  });

  const counts = {
    all: stores.length,
    active: stores.filter((s) => s.plan === "active" && !s.disabled).length,
    trial: stores.filter((s) => s.plan === "trial" && !getTrialInfo(s).isExpired && !s.disabled).length,
    expired: stores.filter((s) => (s.plan === "expired" || (s.plan === "trial" && getTrialInfo(s).isExpired)) && !s.disabled).length,
    disabled: stores.filter((s) => s.disabled).length,
  };

  const getStatusBadge = (store: RegisteredStore) => {
    if (store.disabled) return { label: "Disabled", color: "#94A3B8", bg: "#F1F5F9", icon: <XCircle size={14} /> };
    if (store.plan === "active") return { label: "Active", color: "#059669", bg: "#ECFDF5", icon: <CheckCircle size={14} /> };
    const trial = getTrialInfo(store);
    if (store.plan === "trial" && !trial.isExpired) return { label: `Trial · ${trial.daysLeft}d left`, color: "#D97706", bg: "#FFFBEB", icon: <Clock size={14} /> };
    return { label: "Expired", color: "#DC2626", bg: "#FEF2F2", icon: <AlertTriangle size={14} /> };
  };

  const handleToggleDisabled = async (store: RegisteredStore) => {
    await updateStoreStatus(store.id, { disabled: !store.disabled });
  };

  const handleActivate = async (store: RegisteredStore) => {
    await updateStoreStatus(store.id, { plan: "active", disabled: false });
  };

  const handleDeactivate = async (store: RegisteredStore) => {
    await updateStoreStatus(store.id, { disabled: true });
  };

  const handleDelete = async (storeId: string) => {
    await deleteStore(storeId);
    setConfirmDelete(null);
    setExpandedStore(null);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="sysadmin-page">
      <header className="sysadmin-header">
        <div className="sysadmin-header-left">
          <div className="sysadmin-logo">
            <Shield size={22} />
          </div>
          <div>
            <h1>System Admin Panel</h1>
            <p>Showroom AiPro · Store Management</p>
          </div>
        </div>
        <div className="sysadmin-header-right">
          <span className="sysadmin-user-label">
            <User size={14} /> {user?.name}
          </span>
          <button className="sysadmin-logout" onClick={handleLogout}>
            <LogOut size={16} /> Log Out
          </button>
        </div>
      </header>

      <div className="sysadmin-content">
        <div className="sysadmin-stats">
          {[
            { label: "Total Stores", value: counts.all, color: "#1B6EF3" },
            { label: "Active", value: counts.active, color: "#059669" },
            { label: "On Trial", value: counts.trial, color: "#D97706" },
            { label: "Expired", value: counts.expired, color: "#DC2626" },
            { label: "Disabled", value: counts.disabled, color: "#94A3B8" },
          ].map((stat) => (
            <div key={stat.label} className="sysadmin-stat-card">
              <span className="sysadmin-stat-value" style={{ color: stat.color }}>{stat.value}</span>
              <span className="sysadmin-stat-label">{stat.label}</span>
            </div>
          ))}
        </div>

        <div className="sysadmin-toolbar">
          <div className="sysadmin-search">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search stores, owners, emails..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="sysadmin-filters">
            {(["all", "active", "trial", "expired", "disabled"] as const).map((f) => (
              <button
                key={f}
                className={`sysadmin-filter ${filterStatus === f ? "active" : ""}`}
                onClick={() => setFilterStatus(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
              </button>
            ))}
          </div>
        </div>

        {filteredStores.length === 0 ? (
          <div className="sysadmin-empty">
            <Store size={40} />
            <p>{stores.length === 0 ? "No stores have registered yet." : "No stores match your filters."}</p>
          </div>
        ) : (
          <div className="sysadmin-store-list">
            {filteredStores.map((store) => {
              const status = getStatusBadge(store);
              const trial = getTrialInfo(store);
              const isExpanded = expandedStore === store.id;

              return (
                <div key={store.id} className={`sysadmin-store-card ${store.disabled ? "disabled" : ""}`}>
                  <div className="sysadmin-store-main" onClick={() => setExpandedStore(isExpanded ? null : store.id)}>
                    <div className="sysadmin-store-info">
                      <div className="sysadmin-store-name-row">
                        <Store size={18} />
                        <h3>{store.storeName}</h3>
                        <span className="sysadmin-status-badge" style={{ color: status.color, background: status.bg }}>
                          {status.icon} {status.label}
                        </span>
                      </div>
                      <div className="sysadmin-store-meta">
                        <span><User size={13} /> {store.firstName} {store.lastName}</span>
                        <span><Mail size={13} /> {store.email}</span>
                        <span><Calendar size={13} /> Registered {formatDate(store.trialStartDate)}</span>
                      </div>
                    </div>
                    <div className="sysadmin-store-actions-mini">
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="sysadmin-store-expanded">
                      <div className="sysadmin-detail-grid">
                        <div className="sysadmin-detail">
                          <label>Owner</label>
                          <span>{store.firstName} {store.lastName}</span>
                        </div>
                        <div className="sysadmin-detail">
                          <label>Username</label>
                          <span>{store.username}</span>
                        </div>
                        <div className="sysadmin-detail">
                          <label>Email</label>
                          <span>{store.email}</span>
                        </div>
                        <div className="sysadmin-detail">
                          <label>Phone</label>
                          <span>{store.phone}</span>
                        </div>
                        <div className="sysadmin-detail">
                          <label>Address</label>
                          <span>{store.storeAddress}, {store.city}, {store.state} {store.zip}</span>
                        </div>
                        <div className="sysadmin-detail">
                          <label>Plan</label>
                          <span style={{ textTransform: "capitalize" }}>{store.plan}</span>
                        </div>
                        <div className="sysadmin-detail">
                          <label>Trial Started</label>
                          <span>{formatDate(store.trialStartDate)}</span>
                        </div>
                        <div className="sysadmin-detail">
                          <label>Trial Ends</label>
                          <span>{formatDate(trial.end)}</span>
                        </div>
                      </div>

                      <div className="sysadmin-store-actions">
                        {!store.disabled && store.plan !== "active" && (
                          <button className="sysadmin-action-btn activate" onClick={() => handleActivate(store)}>
                            <CheckCircle size={16} /> Activate Subscription
                          </button>
                        )}
                        {!store.disabled && store.plan !== "active" && (
                          extendTrialStore === store.id ? (
                            <div className="sysadmin-extend-trial">
                              <label>Extend by:</label>
                              <div className="sysadmin-extend-options">
                                {[7, 14, 30].map((d) => (
                                  <button
                                    key={d}
                                    className={`sysadmin-extend-chip ${extendDays === d ? "selected" : ""}`}
                                    onClick={() => setExtendDays(d)}
                                  >
                                    {d} days
                                  </button>
                                ))}
                              </div>
                              <div className="sysadmin-extend-actions">
                                <button
                                  className="sysadmin-action-btn activate"
                                  onClick={async () => {
                                    await extendTrial(store.id, extendDays);
                                    setExtendTrialStore(null);
                                    setExtendDays(7);
                                  }}
                                >
                                  <CheckCircle size={14} /> Confirm +{extendDays} Days
                                </button>
                                <button
                                  className="sysadmin-action-btn cancel"
                                  onClick={() => { setExtendTrialStore(null); setExtendDays(7); }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button className="sysadmin-action-btn extend" onClick={() => setExtendTrialStore(store.id)}>
                              <CalendarPlus size={16} /> Extend Trial
                            </button>
                          )
                        )}
                        {!store.disabled ? (
                          <button className="sysadmin-action-btn deactivate" onClick={() => handleDeactivate(store)}>
                            <ToggleRight size={16} /> Deactivate Store
                          </button>
                        ) : (
                          <button className="sysadmin-action-btn enable" onClick={() => handleToggleDisabled(store)}>
                            <ToggleLeft size={16} /> Enable Store
                          </button>
                        )}
                        {confirmDelete === store.id ? (
                          <div className="sysadmin-confirm-delete">
                            <span>Delete permanently?</span>
                            <button className="sysadmin-action-btn delete-confirm" onClick={() => handleDelete(store.id)}>
                              Yes, Delete
                            </button>
                            <button className="sysadmin-action-btn cancel" onClick={() => setConfirmDelete(null)}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button className="sysadmin-action-btn delete" onClick={() => setConfirmDelete(store.id)}>
                            <Trash2 size={16} /> Delete Store
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
